"use client";

import { useState, type MouseEvent } from "react";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { LogIn, LogOut } from "lucide-react";
import { ChevronDown } from "lucide-react";
import { useUserStore } from "@/store/userStore";
import LoginDialog from "./LoginDialog";

function getInitials(name: string) {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export default function UserMenu() {
  const { user, logout } = useUserStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loginOpen, setLoginOpen] = useState(false);

  const handleOpen = (e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <IconButton onClick={handleOpen} data-testid="user-avatar" sx={{ p: 0, gap: 0.5 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: 13,
            fontWeight: 600,
            bgcolor: user ? "primary.main" : "#dfe1e9",
            color: user ? "#fff" : "#555",
          }}
        >
          {user ? getInitials(user.name) : null}
        </Avatar>
        <ChevronDown size={14} color="#666" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        slotProps={{ paper: { sx: { width: 240, mt: 1, borderRadius: 2.5 } } }}
      >
        {user ? (
          [
            <Box key="current" sx={{ px: 2, py: 1.25 }}>
              <Typography variant="subtitle2">{user.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {user.email}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", textTransform: "capitalize" }}>
                {user.role}
              </Typography>
            </Box>,
            <Divider key="div" />,
            <MenuItem
              key="logout"
              data-testid="logout-button"
              onClick={() => {
                logout();
                handleClose();
              }}
            >
              <LogOut size={18} style={{ marginRight: 12, color: "#666" }} />
              <Typography sx={{ fontSize: 13 }}>Log out</Typography>
            </MenuItem>,
          ]
        ) : (
          <MenuItem
            onClick={() => {
              setLoginOpen(true);
              handleClose();
            }}
          >
            <LogIn size={18} style={{ marginRight: 12, color: "#666" }} />
            <Typography sx={{ fontSize: 13 }}>Log in</Typography>
          </MenuItem>
        )}
      </Menu>

      <LoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} />
    </>
  );
}
